/** @jsxImportSource @emotion/react */

import {
  Card,
  Checkbox,
  Errors,
  Input,
  ListItem,
  Select,
  Setters,
  UtilityButton,
  createStyles,
  useTheme,
} from '@quickbit/qb-design-system';
import { uniqueId } from 'lodash';
import { RelatedPersonFormData } from '../types';
import { ContactInformationForm } from './ContactInformationForm';
import { DateInput } from './DateInput';

interface Props {
  data: RelatedPersonFormData[];
  editable: boolean;
  setters: Setters<RelatedPersonFormData[]>;
  errors: Errors<RelatedPersonFormData[]>;
  onBlur: (key: string, value: string) => void;
}

export const RelatedPeople = ({
  data,
  errors,
  editable,
  setters,
  onBlur,
}: Props) => {
  const { spacing } = useTheme();
  const styles = useStyles();

  return (
    <>
      <div css={{ display: 'grid', gap: spacing.s }}>
        <ListItem
          css={[styles.listItem, !editable && styles.listItemDisabled]}
          title="Are any of the members of the senior management team, board of directors, or the ultimate beneficial owners considered as PEP or RCA? Check if yes."
          icon={<Checkbox checked={data.length > 0} />}
          disabled={!editable}
          onClick={() => {
            if (data.length) {
              setters.set([]);
              return;
            }
            setters.set([
              {
                dateOfBirth: null,
                firstName: '',
                isPep: true,
                lastName: '',
                id: uniqueId('new-'),
                role: '',
                placeOfBirth: '',
                address: {
                  postalRegion: '',
                  postalNumber: '',
                  country: '',
                  address1: '',
                },
                type: '',
                ssn: '',
              },
            ]);
          }}
        />
        {data.map(
          (
            {
              firstName,
              lastName,
              isPep,
              dateOfBirth,
              id,
              address,
              type,
              role,
              ssn,
            },
            idx
          ) => (
            <Card key={id}>
              <div
                css={{
                  display: 'grid',
                  gap: spacing.xxs,
                }}
              >
                {isPep && (
                  <>
                    <Select
                      label="Type"
                      options={[
                        {
                          label: 'Ultimate beneficial owner',
                          value: 'ULTIMATE_BENEFICIAL_OWNER',
                        },
                        { label: 'Board member', value: 'BOARD_MEMBER' },
                        {
                          label: 'Senior management',
                          value: 'SENIOR_MANAGEMENT',
                        },
                        { label: 'Shareholder', value: 'SHAREHOLDER' },
                      ]}
                      values={[type]}
                      onSelect={([type]) => setters.list[idx].type.set(type)}
                      helperText={errors[idx].type ?? undefined}
                      error={!!errors[idx].type}
                    />
                    <Input
                      label="First name"
                      value={firstName}
                      disabled={!editable}
                      onChange={(e) =>
                        setters.list[idx].firstName.set(e.target.value)
                      }
                      onBlur={(e) => onBlur('firstName', e.target.value)}
                      error={!!errors[idx].firstName}
                      helperText={errors[idx].firstName}
                    />
                    <Input
                      label="Last name"
                      value={lastName}
                      disabled={!editable}
                      onChange={(e) =>
                        setters.list[idx].lastName.set(e.target.value)
                      }
                      onBlur={(e) => onBlur('lastName', e.target.value)}
                      error={!!errors[idx].lastName}
                      helperText={errors[idx].lastName}
                    />
                    <DateInput
                      title="Date of birth"
                      value={dateOfBirth}
                      disabled={!editable}
                      onChange={(d) => setters.list[idx].dateOfBirth.set(d)}
                      error={!!errors[idx].dateOfBirth}
                      helperText={errors[idx].dateOfBirth ?? ''}
                    />
                    <Input
                      label="Social security number"
                      value={ssn}
                      disabled={!editable}
                      onChange={(e) =>
                        setters.list[idx].ssn.set(e.target.value)
                      }
                      onBlur={(e) => onBlur('ssn', e.target.value)}
                      error={!!errors[idx].ssn}
                      helperText={errors[idx].ssn}
                    />
                    <ContactInformationForm
                      label="Address"
                      data={address}
                      editable={editable}
                      setters={setters.list[idx].address}
                      errors={errors[idx].address}
                      onBlur={() => {}}
                    />
                    {type && type !== 'SHAREHOLDER' && (
                      <>
                        <Input
                          label="Role"
                          value={role}
                          disabled={!editable}
                          onChange={(e) =>
                            setters.list[idx].role.set(e.target.value)
                          }
                          onBlur={(e) => onBlur('role', e.target.value)}
                          error={!!errors[idx].role}
                          helperText={errors[idx].role}
                        />
                      </>
                    )}
                  </>
                )}
                <UtilityButton
                  text="Remove"
                  onClick={() => setters.set(data.filter((p) => p.id !== id))}
                  disabled={!editable}
                />
              </div>
            </Card>
          )
        )}
        {data.length > 0 && (
          <UtilityButton
            text="Add person"
            icon={'plus'}
            onClick={() =>
              setters.set([
                ...data,
                {
                  dateOfBirth: null,
                  firstName: '',
                  isPep: true,
                  lastName: '',
                  id: uniqueId('new-'),
                  role: '',
                  placeOfBirth: '',
                  address: {
                    postalRegion: '',
                    postalNumber: '',
                    country: '',
                    address1: '',
                  },
                  type: '',
                  ssn: '',
                },
              ])
            }
            disabled={!editable}
          />
        )}
      </div>
    </>
  );
};

const useStyles = createStyles(({ spacing, palette }) => ({
  listItem: {
    borderRadius: spacing.xxxs,
    border: `1px solid ${palette.grey.x200}`,
    ':hover': {
      borderColor: palette.grey.x600,
    },
  },
  listItemDisabled: {
    ':hover': {
      borderColor: 'inherit',
    },
  },
}));
